import {MODALS_IDS} from '@Components/modal-container';
import {openPanelInModal} from '@Components/modal-container/modal-container-reducer';
import {EventUpsellType, openEventsUpsellDialog} from '@Libraries/events.library';
import {openTicketingComingSoonModal} from '@Components/event-wizard/event-wizard-ticketing/ticketing-gating.helper';

// Keep in sync with App\Libraries\Facades\Event\EventTicketing\Enums\TicketingBlockedReason (backend is canonical).
export enum TicketingBlockedReason {
  PLAN = 'plan',
  COUNTRY = 'country',
  FEATURE = 'feature',
}

const TICKETING_ONBOARDING_ENDPOINT = 'eventticketing/getTicketingOnboardingLink';
// The backend reads these as strings and compares them to '1' — a boolean would not match.
const FLAG_PARAM_VALUE = '1';

interface TicketingOnboardingLink {
  isAllowed: boolean;
  blockedReason: TicketingBlockedReason | null;
  needsOnboarding: boolean;
  // false = never submitted (initial CTA); true = submitted (isVerifying then splits verifying vs needs-more).
  detailsSubmitted: boolean;
  // true = details submitted and Stripe is verifying (nothing due, show "please wait"); false = action required.
  isVerifying: boolean;
  // Whether the current Events & Ticketing terms version is on record for this organiser.
  hasAcceptedTerms: boolean;
  // Only ever populated by startTicketingOnboarding — a status read never carries one.
  connectUrl: string | null;
}

/**
 * Asks the backend whether the organiser still needs Stripe onboarding. Read-only: it cannot create a
 * Stripe account or a Hi.Events account, which is why every passive surface — the wizard's step 2→3
 * gate, the Stripe tab, the verifying poll — uses this and not startTicketingOnboarding.
 *
 * Pass recordTermsAcceptance from the panel's "Try again", which re-sends the ticked consent box so a
 * failed acceptance write can be repaired. That must stay separate from starting onboarding: a popup
 * blocked by the browser lands in the ERROR state without the organiser ever reaching Stripe, and its
 * "Try again" must not mint an account.
 */
export const fetchTicketingOnboardingStatus = async (recordTermsAcceptance = false): Promise<TicketingOnboardingLink> => {
  const params = recordTermsAcceptance ? {acceptTerms: FLAG_PARAM_VALUE} : {};
  return (await window.PMW.writeLocal(TICKETING_ONBOARDING_ENDPOINT, params)) as TicketingOnboardingLink;
};

/**
 * The organiser has ticked the terms box and clicked Connect. This is the only call in the frontend
 * that can create a live Stripe Connect account, and it returns the Stripe-hosted Account Link to send
 * them to. Hi.Events owns the account and mints the link; we just relay it.
 */
export const startTicketingOnboarding = async (): Promise<TicketingOnboardingLink> => {
  return (await window.PMW.writeLocal(TICKETING_ONBOARDING_ENDPOINT, {
    acceptTerms: FLAG_PARAM_VALUE,
    startOnboarding: FLAG_PARAM_VALUE,
  })) as TicketingOnboardingLink;
};

/**
 * The user-facing message the backend attached to a failed onboarding call, or null when it carried none.
 *
 * writeLocal rejects with an Error whose message is the ticketing error envelope's user message, so a
 * refusal the organiser can act on (their consent could not be recorded) arrives already worded. A
 * network drop or a parse failure has no such message and falls back to the panel's generic copy.
 */
export const getTicketingOnboardingErrorMessage = (error: unknown): string | null => {
  return error instanceof Error && error.message !== '' ? error.message : null;
};

/**
 * Hard onboarding gate for the step 2→3 transition on paid ticketing. The verdict is server-side
 * because window globals can be stale after an AJAX login. If the organiser is not allowed
 * (plan/country), opens the matching dialog and returns. If allowed and already onboarded, calls
 * onComplete immediately (advance). If allowed but still needs onboarding, opens the onboarding dialog
 * — passing onComplete so the dialog advances to Review itself once onboarding is confirmed. Rejects
 * on a failed check so the caller can keep the user on step 2.
 *
 * This is a status read and creates nothing: only the organiser's Connect click inside the dialog can
 * mint a Stripe account.
 */
export const runTicketingOnboardingGate = async (onComplete: () => void): Promise<void> => {
  const onboarding = await fetchTicketingOnboardingStatus();
  if (!onboarding.isAllowed) {
    if (onboarding.blockedReason === TicketingBlockedReason.COUNTRY || onboarding.blockedReason === TicketingBlockedReason.FEATURE) {
      openTicketingComingSoonModal(onboarding.blockedReason === TicketingBlockedReason.COUNTRY);
    } else {
      openEventsUpsellDialog(EventUpsellType.TICKETING, undefined);
    }
    return;
  }
  if (!onboarding.needsOnboarding) {
    onComplete();
    return;
  }
  window.PMW.redux.store.dispatch(
    openPanelInModal({
      modalId: MODALS_IDS.TICKETING_STRIPE_ONBOARDING_MODAL,
      panelProps: {
        onComplete,
        detailsSubmitted: onboarding.detailsSubmitted,
        isVerifying: onboarding.isVerifying,
        hasAcceptedTerms: onboarding.hasAcceptedTerms,
      },
    })
  );
};
